import React, { Component, ErrorInfo, ReactNode } from "react";

// NOTE: This component intentionally uses native HTML buttons instead of
// importing Button from @/components/ui/button to avoid circular dependency
// and ES module initialization order issues in production builds.

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen w-full flex flex-col items-center justify-center p-4 bg-background text-foreground text-center">
          <h1 className="text-4xl font-bold mb-4">Something went wrong</h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-md">
            We apologize for the inconvenience. The application has encountered an unexpected error.
          </p>
          {this.state.error && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-md mb-8 max-w-lg overflow-auto text-left w-full font-mono text-sm max-h-40">
              {this.state.error.message}
            </div>
          )}
          <div className="flex gap-4">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-bold h-10 px-4 py-2 border-2 border-black bg-[#FF6B9D] text-black shadow-[4px_4px_0_black] hover:shadow-[2px_2px_0_black] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            >
              Reload Page
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-bold h-10 px-4 py-2 border-2 border-foreground bg-background text-foreground hover:bg-accent"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
